/* MC_PDP_CASETYPECARDS_FRAGOLA: ABConvert assigns the arm; the theme renders it. */
(() => {
  const testId = 'MC_PDP_CASETYPECARDS_FRAGOLA';
  const activationClass = 'maison-case-type-variation';
  const rootSelector = `[maison-ab-id="${testId}"]`;
  const productRequests = new Map();
  const states = new WeakMap();
  const reviewParams = new URLSearchParams(location.search);
  let assignment = null;
  const swatchType = (item) => (item.getAttribute('data-value') || '').trim().toUpperCase();
  const storefrontUrl = (target) => {
    const source = new URL(target, location.origin);
    return new URL(`${source.pathname}${source.search}${source.hash}`, location.origin);
  };

  function armFor(root) {
    if (reviewParams.get('maison_ab_preview') === testId
      && root.dataset.reviewThemeId === root.dataset.reviewThemeAllowed
      && root.dataset.reviewThemeRole !== 'main') {
      const arm = reviewParams.get('maison_ab_arm');
      if (arm === 'variation' || arm === 'control') return arm;
    }
    const current = window.ABConvert?.getAssignment?.('54351') || assignment;
    if (current?.testGroup) return current.testGroup.control || current.testGroup.index === 0 ? 'control' : 'variation';
    return document.documentElement.classList.contains(activationClass) ? 'variation' : 'control';
  }

  function status(root, phase, message) {
    root.dataset.maisonCaseState = phase;
    const label = root.closest('.product-info-inner')?.querySelector('[data-maison-preview-state]');
    if (label && label.textContent !== message) label.textContent = message;
    // Opt-in diagnostics are restricted to the review theme, never shoppers.
    if (label && reviewParams.get('maison_ab_debug') === '1') {
      document.title = `Case cards ${JSON.stringify({
        phase, arm: armFor(root), theme: root.dataset.reviewThemeId, source: root.dataset.maisonCaseSource,
        display: getComputedStyle(root).display, width: root.getBoundingClientRect().width,
        cards: [...root.querySelectorAll('[data-case]')].filter((card) => !card.hidden).length,
        message
      })}`;
    }
  }

  function show(root, active) {
    const info = root.closest('.product-info-inner');
    if (!info) return;
    info.classList.toggle('maison-case-type-active', active);
    info.querySelector('.product-variant-picker')?.classList.toggle('maison-case-type-replaced', active);
    caseSwatches(info)?.classList.toggle('maison-case-type-replaced', active);
  }

  function caseSwatches(info) {
    return [...info.querySelectorAll('fieldset')].find((field) => {
      const values = [...field.querySelectorAll('.swatch-group-selector[swatch-url]')]
        .map(swatchType);
      return values.includes('SNAP') && values.includes('TOUGH') && values.includes('TOUGH MAGSAFE');
    });
  }

  async function renderSection(root, target, { historyMode = 'push', focusSelector, loadingButton } = {}) {
    if (root.dataset.loading === 'true') return false;
    const section = root.closest('.section-main-product');
    const info = root.closest('.product-info-inner');
    const swatches = info && caseSwatches(info);
    if (!section) return false;
    root.dataset.loading = 'true';
    if (loadingButton) loadingButton.dataset.loading = 'true';
    const error = root.querySelector('[data-update-error]');
    error.hidden = true;

    try {
      const startedAt = performance.now();
      const request = storefrontUrl(target);
      request.searchParams.set('section_id', section.id.replace('shopify-section-', ''));
      if (root.dataset.reviewThemeRole === 'unpublished' && root.dataset.reviewThemeId === root.dataset.reviewThemeAllowed) {
        request.searchParams.set('preview_theme_id', root.dataset.reviewThemeId);
      }
      const response = await fetch(request);
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      const documentForVariant = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextSection = documentForVariant.querySelector('.section-main-product');
      const nextInfo = nextSection?.querySelector('.product-info-inner');
      const nextRoot = nextInfo?.querySelector(rootSelector);
      if (!nextRoot || nextSection.id !== section.id) throw new Error('Product section is unavailable');

      // Keep the feedback visible when a cached section arrives almost instantly.
      await new Promise((resolve) => setTimeout(resolve, Math.max(0, 240 - (performance.now() - startedAt))));

      // The case-type app builds these links on page load. Keep its hidden group as the
      // source of the linked products when Shopify swaps the product section.
      if (swatches) nextInfo.insertBefore(swatches, nextRoot);
      section.replaceWith(nextSection);
      if (historyMode === 'push') history.pushState({ ...(history.state || {}), maisonCaseType: true }, '', target);
      if (historyMode === 'replace') history.replaceState({ ...(history.state || {}), maisonCaseType: true }, '', target);
      start(nextRoot, focusSelector);
      return true;
    } catch (cause) {
      console.error(testId, cause);
      if (cause instanceof TypeError) {
        // Some browser/network policies reject fetch while normal navigation works.
        location.assign(storefrontUrl(target));
        return false;
      }
      error.hidden = false;
      return false;
    } finally {
      root.dataset.loading = 'false';
      if (loadingButton) delete loadingButton.dataset.loading;
    }
  }

  function start(root, focusSelector) {
    let state = states.get(root);
    if (!state) { state = { running: false, ready: false, attempts: 0, timer: null }; states.set(root, state); }
    if (armFor(root) !== 'variation') {
      clearTimeout(state.timer);
      state.timer = null;
      show(root, false);
      status(root, 'control', 'Original selector · choose “Show new cards” to review the variant.');
      return;
    }
    if (state.ready) { show(root, true); status(root, 'ready', 'New cards active'); return; }
    if (state.running || state.timer) return;
    state.running = true;
    state.attempts += 1;
    status(root, 'waiting-swatches', 'Loading case options…');
    initialize(root).then((ready) => {
      state.ready = Boolean(ready);
      if (ready && armFor(root) === 'variation') {
        show(root, true);
        status(root, 'ready', 'New cards active');
        if (focusSelector) root.querySelector(focusSelector)?.focus();
      }
    }).catch((error) => {
      console.error(testId, error);
      status(root, 'error', `Could not load case options: ${error.message}`);
    }).finally(() => {
      state.running = false;
      if (!state.ready && root.isConnected && armFor(root) === 'variation' && state.attempts < 4) {
        state.timer = setTimeout(() => { state.timer = null; start(root, focusSelector); }, 500 * state.attempts);
      }
    });
  }

  async function initialize(root) {
    const info = root.closest('.product-info-inner');
    if (!info) return;
    const types = ['SNAP', 'TOUGH', 'TOUGH MAGSAFE'];
    const embedded = root.querySelector('[data-case-products]');
    let products = embedded ? JSON.parse(embedded.textContent) : null;
    root.dataset.maisonCaseSource = products ? 'embedded' : 'fetch';
    if (products && types.some((type) => !products[type]?.id || !Array.isArray(products[type].variants))) {
      throw new Error('Embedded case product data is incomplete');
    }
    const swatchFieldset = products ? caseSwatches(info) : await new Promise((resolve) => {
      const find = () => caseSwatches(info);
      const present = find();
      if (present) return resolve(present);
      const observer = new MutationObserver(() => {
        if (!root.isConnected) { observer.disconnect(); resolve(null); return; }
        const found = find();
        if (found) { observer.disconnect(); resolve(found); }
      });
      // Swatch King can finish after the rest of the page. Keep waiting while this
      // product is mounted, including when the app fills in attributes in stages.
      observer.observe(document.documentElement, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['class', 'data-value', 'swatch-url']
      });
    });
    if (!products && !swatchFieldset) return false;

    const links = products
      ? Object.fromEntries(types.map((type) => [type, storefrontUrl(products[type].url).toString()]))
      : Object.fromEntries([...swatchFieldset.querySelectorAll('.swatch-group-selector[swatch-url]')]
        .map((item) => [swatchType(item), storefrontUrl(item.getAttribute('swatch-url')).toString()]));
    if (types.some((type) => !links[type])) throw new Error('Case-type product links are incomplete');
    if (!products) {
      products = Object.fromEntries(await Promise.all(types.map(async (type) => {
        if (!productRequests.has(links[type])) {
          const productUrl = storefrontUrl(links[type]);
          productUrl.pathname = `${productUrl.pathname.replace(/\/$/, '')}.js`;
          productRequests.set(links[type], fetch(productUrl, { mode: 'same-origin', credentials: 'same-origin' }).then((response) => {
            if (!response.ok) throw new Error(`${type} variants returned HTTP ${response.status}`);
            return response.json();
          }).catch((error) => {
            productRequests.delete(links[type]);
            throw new Error(`${type} ${productUrl.pathname}: ${error.message}`);
          }));
        }
        return [type, await productRequests.get(links[type])];
      })));
    }

    if (!root.isConnected || armFor(root) !== 'variation') return false;

    const nativePicker = info.querySelector('.product-variant-picker');
    const currentModel = nativePicker?.querySelector('input[name="options[Model]"]')?.value;
    if (!currentModel) throw new Error('Phone-model selector is not ready');
    const currentType = types.find((type) => String(products[type].id) === root.dataset.productId)
      || types.find((type) => storefrontUrl(links[type]).pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, ''));
    if (!types.includes(currentType)) throw new Error('Current product is not in the case-type group');
    const modelVariant = (type, model) => products[type]?.variants.find((variant) => variant.available && variant.title === model);
    const urlFor = (type, model) => {
      const variant = modelVariant(type, model);
      if (!variant) return null;
      const url = storefrontUrl(links[type]);
      const existing = new URLSearchParams(location.search);
      if (root.dataset.reviewThemeRole === 'unpublished' && root.dataset.reviewThemeId === root.dataset.reviewThemeAllowed) {
        existing.set('preview_theme_id', root.dataset.reviewThemeId);
      }
      existing.set('variant', variant.id);
      url.search = existing.toString();
      return url.toString();
    };

    const trigger = root.querySelector('.maison-case-type-test__model-trigger');
    const dialog = root.querySelector('dialog');
    const search = dialog.querySelector('input[type="search"]');
    const list = dialog.querySelector('.maison-case-type-test__models');
    const money = (minor) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: window.Shopify?.currency?.active || 'EUR'
    }).format(minor / 100);
    trigger.querySelector('[data-model-label]').textContent = currentModel;
    for (const button of root.querySelectorAll('[data-case]')) {
      const type = button.dataset.case;
      const variant = modelVariant(type, currentModel);
      button.hidden = !variant;
      if (!variant) continue;
      const previousType = type === 'TOUGH' ? 'SNAP' : type === 'TOUGH MAGSAFE' ? 'TOUGH' : null;
      const previousVariant = previousType && modelVariant(previousType, currentModel);
      const difference = previousVariant ? variant.price - previousVariant.price : 0;
      const caption = !previousType ? 'Base price' : previousVariant
        ? `${difference >= 0 ? '+' : '−'}${money(Math.abs(difference))} vs ${previousType}` : 'Price';
      const compareAt = variant.compare_at_price > variant.price ? variant.compare_at_price : null;
      button.setAttribute('aria-pressed', String(type === currentType));
      button.dataset.onSale = String(Boolean(compareAt));
      button.querySelector('[data-price-caption]').textContent = caption;
      button.querySelector('[data-price]').textContent = money(variant.price);
      button.querySelector('[data-mobile-price-caption]').textContent = caption;
      button.querySelector('[data-mobile-price]').textContent = money(variant.price);
      for (const compare of button.querySelectorAll('[data-compare-price], [data-mobile-compare-price]')) {
        compare.hidden = !compareAt;
        if (compareAt) compare.textContent = money(compareAt);
      }
      button.addEventListener('click', () => {
        if (type === currentType) {
          button.dataset.loading = 'true';
          setTimeout(() => { delete button.dataset.loading; }, 240);
          return;
        }
        renderSection(root, urlFor(type, currentModel), {
          focusSelector: `[data-case="${type}"]`, loadingButton: button
        });
      });
    }

    const models = [...new Set(types.flatMap((type) => products[type].variants
      .filter((variant) => variant.available).map((variant) => variant.title)))];
    function renderModels() {
      const query = search.value.trim().toLocaleLowerCase();
      list.replaceChildren(...models.filter((model) => model.toLocaleLowerCase().includes(query)).map((model) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', String(model === currentModel));
        button.textContent = model;
        button.addEventListener('click', async () => {
          if (root.dataset.loading === 'true') return;
          if (model === currentModel) {
            button.dataset.loading = 'true';
            setTimeout(closeDialog, 240);
            return;
          }
          const type = modelVariant(currentType, model) ? currentType
            : types.find((candidate) => modelVariant(candidate, model));
          const target = urlFor(type, model);
          if (target) {
            const updated = await renderSection(root, target, {
              focusSelector: '.maison-case-type-test__model-trigger', loadingButton: button
            });
            if (!updated) closeDialog();
          }
        });
        return button;
      }));
    }
    const closeDialog = () => {
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      dialog.classList.add('is-closing');
      setTimeout(() => { if (dialog.open) dialog.close(); dialog.classList.remove('is-closing'); }, 260);
    };
    trigger.addEventListener('click', () => { search.value = ''; renderModels(); dialog.showModal(); search.focus(); });
    search.addEventListener('input', renderModels);
    dialog.querySelector('.maison-case-type-test__close').addEventListener('click', closeDialog);
    dialog.querySelector('.maison-case-type-test__back').addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(); });
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); closeDialog(); });

    swatchFieldset?.classList.add('maison-case-type-replaced');
    nativePicker.classList.add('maison-case-type-replaced');
    info.classList.add('maison-case-type-active');
    return true;
  }

  const boot = () => {
    document.querySelectorAll(rootSelector).forEach((root) => start(root));
    if (document.querySelector(rootSelector)) {
      history.replaceState({ ...(history.state || {}), maisonCaseType: true }, '');
    }
  };
  const sync = () => document.querySelectorAll(rootSelector).forEach((root) => start(root));
  // ABConvert skips assignment-ready for preview overrides, so use its ready
  // queue/API as well as live assignment events. Never infer the arm from timing.
  (window.ABConvertQueue = window.ABConvertQueue || []).push(sync);
  window.addEventListener('abconvert:ready', sync);
  window.addEventListener('abconvert:assignment-ready', (event) => {
    if (String(event.detail?.experimentId) !== '54351') return;
    assignment = event.detail;
    sync();
  });
  window.addEventListener('pageshow', sync);
  document.addEventListener('shopify:section:load', sync);
  document.addEventListener('variant:change', sync);
  document.addEventListener('vsk:product:variant:changed', sync);
  new MutationObserver(sync).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  // Section-rendered script tags are inert. The head-loaded controller must
  // initialize newly inserted roots, even when their HTML has copied data flags.
  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => [...mutation.addedNodes].some((node) => node.nodeType === 1
      && (node.matches(`${rootSelector}, .swatch-group-selector[swatch-url]`)
        || node.querySelector(`${rootSelector}, .swatch-group-selector[swatch-url]`))))) sync();
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('popstate', (event) => {
    const root = document.querySelector(rootSelector);
    if (event.state?.maisonCaseType && root) renderSection(root, location.href, { historyMode: 'none' });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
